import * as XLSX from 'xlsx';

export interface ParsedTaskRow {
  title: string;
  categoryText: string;
  startDate: string;
  dueDate: string;
}

const HEADER_ALIASES: Record<'title' | 'category' | 'startDate' | 'dueDate', string[]> = {
  title: ['title', 'task name', 'task', 'name'],
  category: ['category', 'type'],
  startDate: ['start date', 'start'],
  dueDate: ['due date', 'end date', 'due', 'end'],
};

function normalizeHeader(value: unknown): string {
  return String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');
}

function pad(value: number): string {
  return String(value).padStart(2, '0');
}

/** Accepts YYYY-MM-DD, D/M/YYYY, Excel serials, and Date cells. */
export function parseImportDate(value: unknown): string {
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return `${value.getFullYear()}-${pad(value.getMonth() + 1)}-${pad(value.getDate())}`;
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    const parsed = XLSX.SSF.parse_date_code(value);
    if (parsed) return `${parsed.y}-${pad(parsed.m)}-${pad(parsed.d)}`;
  }
  const text = String(value ?? '').trim();
  if (!text) return '';
  const iso = text.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`;
  const dmy = text.match(/^(\d{1,2})[/. -](\d{1,2})[/. -](\d{4})$/);
  if (dmy) return `${dmy[3]}-${pad(Number(dmy[2]))}-${pad(Number(dmy[1]))}`;
  return '';
}

function rowsFromMatrix(matrix: unknown[][]): ParsedTaskRow[] {
  if (matrix.length === 0) return [];
  const headers = (matrix[0] ?? []).map(normalizeHeader);
  const indexOf = (field: keyof typeof HEADER_ALIASES): number =>
    headers.findIndex((header) => HEADER_ALIASES[field].includes(header));

  const titleIndex = indexOf('title');
  const categoryIndex = indexOf('category');
  const startIndex = indexOf('startDate');
  const dueIndex = indexOf('dueDate');
  if (titleIndex < 0) {
    throw new Error('The sheet needs a Title column');
  }

  const rows: ParsedTaskRow[] = [];
  for (const line of matrix.slice(1)) {
    const title = String(line?.[titleIndex] ?? '').trim();
    const categoryText = categoryIndex >= 0 ? String(line?.[categoryIndex] ?? '').trim() : '';
    const startDate = startIndex >= 0 ? parseImportDate(line?.[startIndex]) : '';
    const dueDate = dueIndex >= 0 ? parseImportDate(line?.[dueIndex]) : '';
    if (!title && !categoryText && !startDate && !dueDate) continue;
    rows.push({ title, categoryText, startDate, dueDate });
  }
  return rows;
}

export async function parseTaskImportFile(file: File): Promise<ParsedTaskRow[]> {
  const name = file.name.toLowerCase();
  if (name.endsWith('.csv') || name.endsWith('.txt')) {
    const text = await file.text();
    const matrix = XLSX.utils.sheet_to_json(XLSX.read(text, { type: 'string', raw: false }).Sheets.Sheet1, {
      header: 1,
      raw: false,
      defval: '',
    }) as unknown[][];
    return rowsFromMatrix(matrix);
  }

  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(buffer, { type: 'array', cellDates: true });
  const sheetName = workbook.SheetNames[0];
  if (!sheetName) throw new Error('The workbook has no sheets');
  const matrix = XLSX.utils.sheet_to_json(workbook.Sheets[sheetName], {
    header: 1,
    raw: true,
    defval: '',
  }) as unknown[][];
  return rowsFromMatrix(matrix);
}

const TEMPLATE_ROWS = [
  ['Title', 'Category', 'Start Date', 'Due Date'],
  ['Business Operations - Define - API/UI Contract', 'Task', '2026-09-23', '2026-09-25'],
  ['Business Operations - API - Implement Paginated List', 'Development', '2026-09-23', '2026-09-30'],
  ['Business Operations - QA - Validate API Contract & Filters', 'Testing', '2026-10-01', '2026-10-03'],
];

export function downloadTaskImportTemplate(format: 'csv' | 'xlsx'): void {
  const sheet = XLSX.utils.aoa_to_sheet(TEMPLATE_ROWS);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, sheet, 'Tasks');
  if (format === 'csv') {
    XLSX.writeFile(workbook, 'task-import-template.csv', { bookType: 'csv' });
    return;
  }
  sheet['!cols'] = [{ wch: 64 }, { wch: 16 }, { wch: 14 }, { wch: 14 }];
  XLSX.writeFile(workbook, 'task-import-template.xlsx');
}
